<template>
  <div class="relative inline-block w-max" ref="triggerRef" :class="customClass">
    <!-- Trigger Button -->
    <button
      @click.stop="toggleDropdown"
      class="dropdown-trigger"
      :class="triggerStyle ?? 'px-4 py-2 bg-blue-500 text-white rounded w-full'"
    >
      <slot name="trigger">Actions</slot>
    </button>

    <!-- Dropdown Content -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="isOpen"
          ref="dropdownRef"
          class="dropdown-menu bg-white shadow-md rounded-lg w-max"
          :style="{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            minWidth: dropdownPosition.minWidth,
          }"
          @click.stop="handleDropdownClick"
        >
          <slot name="contents"></slot>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { activeDropdownId, openDropdownHierarchy } from '@/composables/globalDropdown';
import { useEventListener } from '@vueuse/core';
import { inject, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';

const props = defineProps<{
  triggerStyle?: string;
  closeOnOutsideClick?: boolean;
  closeOnSelect?: boolean;
  customClass?: string;
}>();
const emits = defineEmits(['open']);
const NAV_HEIGHT = 70; // adjust as per your navbar height
const id = Math.random().toString(36).substring(2, 9);
const parentId = inject<string | null>('dropdownParentId', null);
provide('dropdownParentId', id);

const isOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);

interface DropdownPositionType {
  top: string;
  left: string;
  minWidth: string;
}

const dropdownPosition = ref<DropdownPositionType>({
  top: '0px',
  left: '0px',
  minWidth: 'auto',
});

const toggleDropdown = async () => {
  if (!isOpen.value) {
    const newHierarchy = parentId
      ? [...openDropdownHierarchy.value.filter((i) => i !== id), id]
      : [id];
    openDropdownHierarchy.value = newHierarchy;
    activeDropdownId.value = id;
  } else {
    openDropdownHierarchy.value = [];
    activeDropdownId.value = null;
  }
};

const positionDropdown = async () => {
  await nextTick();

  requestAnimationFrame(() => {
    if (!triggerRef.value || !dropdownRef.value) return;

    const triggerRect = triggerRef.value.getBoundingClientRect();
    const dropdownRect = dropdownRef.value.getBoundingClientRect();

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;

    const top =
      spaceBelow >= dropdownRect.height
        ? triggerRect.bottom + window.scrollY
        : triggerRect.top - dropdownRect.height + window.scrollY;

    const left =
      spaceLeft >= dropdownRect.width
        ? triggerRect.left + triggerRect.width - dropdownRect.width
        : triggerRect.left;

    dropdownPosition.value = {
      top: `${top}px`,
      left: `${left}px`,
      minWidth: `${triggerRect.width}px`,
    };
  });
};

const handleDropdownClick = () => {
  if (props.closeOnSelect !== false) {
    const index = openDropdownHierarchy.value.indexOf(id);
    if (index !== -1) {
      openDropdownHierarchy.value = openDropdownHierarchy.value.slice(0, index);
      activeDropdownId.value =
        openDropdownHierarchy.value[openDropdownHierarchy.value.length - 1] ?? null;
    }
  }
};

const handleOutsideClick = (event: MouseEvent) => {
  if (props.closeOnOutsideClick === false) return;

  const target = event.target as HTMLElement;
  const clickedInside = triggerRef.value?.contains(target) || dropdownRef.value?.contains(target);

  if (!clickedInside) {
    openDropdownHierarchy.value = [];
    activeDropdownId.value = null;
  }
};

const isElementInViewport = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();

  return (
    rect.top >= NAV_HEIGHT &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  );
};

const handleScroll = () => {
  if (!triggerRef.value) return;

  if (!isElementInViewport(triggerRef.value)) {
    if (isOpen.value) {
      openDropdownHierarchy.value = [];
      activeDropdownId.value = null;
    }
  } else if (isOpen.value) {
    positionDropdown();
  }
};

watch(openDropdownHierarchy, async (chain) => {
  const willBeOpen = chain.includes(id);
  isOpen.value = willBeOpen;
  emits('open', willBeOpen);
});

watch(isOpen, async (visible) => {
  if (visible) {
    await positionDropdown();
  }
});

onMounted(() => {
  useEventListener(document, 'click', handleOutsideClick);

  window.addEventListener('scroll', handleScroll, true);
  useEventListener(window, 'resize', handleScroll, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', handleScroll);
});
</script>

<style scoped>
.dropdown-trigger {
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-menu {
  z-index: 10030;
  border: 1px solid #e5e7eb;
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

/* Vue transition classes */
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
</style>
